import React from 'react';

import { Box, Stack, Typography } from '@mui/material';
import ReactJson from 'react-json-view';

import useStyles from './style';

/**
 * ChangeTypeDetails component displays details of different change types (added, deleted, modified)
 * for a specific feature.
 *
 * @param {Object} props - The props for the component.
 * @param {Object} props.changeDetails - Details of the changes.
 * @param {string} props.featureKey - The key identifying the feature in the changes.
 * @param {Function} props.handleModalOpen - Function to handle modal open events.
 * @returns {JSX.Element} - Rendered component.
 */
const ChangeTypeDetails = ({ changeDetails, featureKey, handleModalOpen }) => {
    const classes = useStyles();

    // Container for the different change types (added, deleted, modified)
    return (
        <Box sx={{ width: '100%' }}>
            <Stack
                spacing={{ xs: 1, sm: 2 }}
                direction="row"
                useFlexGap
                sx={{ flexWrap: 'wrap' }}
            >
                {['added', 'deleted', 'modified'].map((updateType) => {
                    const updateData = changeDetails.changes[featureKey]?.[updateType];

                    return (
                        updateData?.length > 0 && (
                            <Box
                                key={updateType}
                                className={classes.updateTypeBox}
                                onClick={() => handleModalOpen(updateData, updateType, featureKey)}
                                sx={{ minWidth: '45%', padding: '1rem' }}
                            >
                                <Typography textTransform={'capitalize'} component="div"
                                    sx={{
                                        fontWeight: 'bold',
                                        position: 'sticky',
                                        top: '-1rem',
                                        backgroundColor: 'white',
                                        zIndex: 1,
                                    }}
                                    variant='h6'
                                >
                                    {updateType}
                                </Typography>
                                <Box mt={.5}>
                                    {updateData.map((item) => (
                                        <Box key={item.path} mb={2}>
                                            <Typography component="span" sx={{ color: '#fc4b58' }}>
                                                Path :
                                            </Typography>
                                            <Typography component="span"
                                                sx={{ color: '#0369a1', fontWeight: 'bold' }}
                                            >
                                                {`  ${item.path}`}
                                            </Typography>
                                            <Box mt={.5} />

                                            {updateType !== 'modified' && (
                                                <>
                                                    <Typography component="span" sx={{ color: '#fc4b58' }}>
                                                        Value :
                                                    </Typography>
                                                    {typeof item.value !== 'object' ? (
                                                        <Typography component="span">
                                                            {`  ${item.value}`}
                                                        </Typography>
                                                    ) : (
                                                        <ReactJson
                                                            name={false}
                                                            enableClipboard={true}
                                                            displayDataTypes={false}
                                                            src={item.value}
                                                        />
                                                    )}
                                                </>
                                            )}

                                            {updateType === 'modified' && (
                                                <>
                                                    <Typography component="span" sx={{ color: '#fc4b58' }}>
                                                        From :
                                                    </Typography>
                                                    {typeof item.from !== 'object' ? (
                                                        <Typography component="span">
                                                            {`  ${item.from}`}
                                                        </Typography>
                                                    ) : (
                                                        <ReactJson
                                                            name={false}
                                                            enableClipboard={true}
                                                            displayDataTypes={false}
                                                            src={item.from}
                                                        />
                                                    )}
                                                    <Box mt={.5} />
                                                    <Typography component="span" sx={{ color: '#fc4b58' }}>
                                                        To :
                                                    </Typography>
                                                    {typeof item.to !== 'object' ? (
                                                        <Typography component="span">
                                                            {`  ${item.to}`}
                                                        </Typography>
                                                    ) : (
                                                        <ReactJson
                                                            name={false}
                                                            enableClipboard={true}
                                                            displayDataTypes={false}
                                                            src={item.to}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ));
                })}
            </Stack>
        </Box>
    );
};

export default ChangeTypeDetails;
